import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtIDR, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Tag, Plus, Pencil, Trash2, Copy, CheckCircle2, XCircle, Search, Package } from "lucide-react";

/* Reusable search dropdown for picking treatments/products (with multi-select bundling). */
function ItemSearchPicker({ items, selected, onToggle, testid }) {
  const [q, setQ] = useState("");
  const key = (it) => `${it.type}:${it.id}`;
  const filtered = q.trim()
    ? items.filter((it) => it.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 40)
    : [];
  return (
    <div>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid={`${testid}-search`} className="pl-9" placeholder="Ketik nama produk atau treatment..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {q.trim() && (
        <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border divide-y" data-testid={`${testid}-results`}>
          {filtered.map((it) => {
            const on = selected.some((x) => key(x) === key(it));
            return (
              <button type="button" key={key(it)} data-testid={`${testid}-opt-${it.id}`}
                onClick={() => onToggle(it)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${on ? "bg-foreground text-background" : "hover:bg-secondary"}`}>
                <span className="flex items-center gap-2">
                  {on && <CheckCircle2 className="h-3.5 w-3.5" />}
                  <Badge variant="outline" className={`text-[10px] ${on ? "border-background/40 text-background" : ""}`}>{it.type === "treatment" ? "Treatment" : "Produk"}</Badge>
                  {it.name}
                </span>
                <span className={on ? "text-background/80" : "text-muted-foreground"}>{fmtIDR(it.normal_price)}</span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">Tidak ada item cocok.</div>}
        </div>
      )}
    </div>
  );
}

export default function Promos() {
  const { branches } = useBranch();
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";

  const [treatments, setTreatments] = useState([]);
  const [products, setProducts] = useState([]);

  const promoItems = useMemo(() => ([
    ...treatments.map((t) => ({ type: "treatment", id: t.id, name: t.name, normal_price: Number(t.price) || 0 })),
    ...products.map((p) => ({ type: "product", id: p.id, name: p.name, normal_price: Number(p.sell_price) || Number(p.unit_cost) || 0 })),
  ]), [treatments, products]);
  const itemKey = (it) => `${it.type}:${it.id}`;
  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;

  // ---------- Item / Bundle promos (Harga Coret) ----------
  const [itemPromos, setItemPromos] = useState([]);
  const [promoName, setPromoName] = useState("");
  const [promoSelectedList, setPromoSelectedList] = useState([]);
  const [promoDiscType, setPromoDiscType] = useState("fixed");
  const [promoPackagePrice, setPromoPackagePrice] = useState("");
  const [promoDiscPct, setPromoDiscPct] = useState("");
  const [promoBranchIds, setPromoBranchIds] = useState([]);
  const [itemBusy, setItemBusy] = useState(false);

  const togglePromoItem = (it) => setPromoSelectedList((list) =>
    list.some((x) => itemKey(x) === itemKey(it)) ? list.filter((x) => itemKey(x) !== itemKey(it)) : [...list, it]);
  const toggleItemPromoBranch = (id) => setPromoBranchIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const promoNormalTotal = promoSelectedList.reduce((s, it) => s + Number(it.normal_price || 0), 0);
  const promoFinalPreview = promoDiscType === "percent"
    ? Math.round(promoNormalTotal * (1 - (Number(promoDiscPct) || 0) / 100))
    : (Number(promoPackagePrice) || 0);

  const loadItemPromos = () => api.get("/item-promos", { params: { active_only: 1 } }).then((r) => setItemPromos(r.data)).catch(() => {});

  const submitItemPromo = async () => {
    if (!promoName.trim()) { toast.error("Nama promo wajib diisi"); return; }
    if (promoSelectedList.length === 0) { toast.error("Pilih minimal satu item (produk/treatment)"); return; }
    const payload = {
      name: promoName.trim(),
      items: promoSelectedList.map((it) => ({ item_type: it.type, item_id: it.id })),
      discount_type: promoDiscType,
      branch_ids: promoBranchIds,
    };
    if (promoDiscType === "percent") {
      const pct = Number(promoDiscPct);
      if (!(pct > 0 && pct < 100)) { toast.error("Diskon persen harus 1–99%"); return; }
      payload.discount_pct = pct;
    } else {
      const price = Number(promoPackagePrice);
      if (promoPackagePrice === "" || !(price >= 0)) { toast.error("Masukkan harga promo yang valid"); return; }
      if (price >= promoNormalTotal) { toast.error("Harga promo harus lebih kecil dari total harga normal"); return; }
      payload.promo_price = price;
    }
    setItemBusy(true);
    try {
      await api.post("/item-promos", payload);
      const kind = promoSelectedList.length > 1 ? "Paket bundling" : "Promo";
      toast.success(`${kind} "${promoName}" aktif & tersinkron ke POS`);
      setPromoName(""); setPromoSelectedList([]); setPromoPackagePrice(""); setPromoDiscPct(""); setPromoBranchIds([]);
      loadItemPromos();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan promo");
    } finally { setItemBusy(false); }
  };

  const deleteItemPromo = async (id) => {
    if (!window.confirm("Hapus promo ini?")) return;
    try { await api.delete(`/item-promos/${id}`); toast.success("Promo dihapus"); loadItemPromos(); }
    catch { toast.error("Gagal menghapus promo"); }
  };

  // ---------- Coupon promos (kode kupon) ----------
  const [promos, setPromos] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [clinicBranches, setClinicBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [couponSelected, setCouponSelected] = useState([]); // targets (treatment/product) for bundling
  const [couponBusy, setCouponBusy] = useState(false);
  const couponFormRef = useRef(null);
  const emptyForm = () => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      name: "", code: "", description: "", promo_type: "discount",
      discount_pct: "", discount_amount: "", redeem_item_name: "", redeem_price: "", min_purchase: "",
      start_at: today, end_at: today, branch_ids: [], active: true, clinic_id: isSuper ? (clinics[0]?.id || "") : "",
    };
  };
  const [form, setForm] = useState(emptyForm());

  const couponNormalTotal = couponSelected.reduce((s, it) => s + Number(it.normal_price || 0), 0);
  const toggleCouponItem = (it) => setCouponSelected((list) =>
    list.some((x) => itemKey(x) === itemKey(it)) ? list.filter((x) => itemKey(x) !== itemKey(it)) : [...list, it]);
  const toggleCouponBranch = (id) => setForm((f) => {
    const arr = f.branch_ids || [];
    return { ...f, branch_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
  });

  const refresh = () => api.get("/promos").then((r) => setPromos(r.data));

  useEffect(() => {
    refresh();
    loadItemPromos();
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/inventory", { params: { category: "retail" } }).then((r) => setProducts(r.data)).catch(() => {});
    if (isSuper) api.get("/admin/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (isSuper && form.clinic_id) {
      api.get(`/admin/clinics/${form.clinic_id}/branches`).then((r) => setClinicBranches(r.data)).catch(() => setClinicBranches([]));
    } else if (!isSuper) {
      setClinicBranches(branches);
    }
  }, [isSuper, form.clinic_id, branches]);

  const resetCoupon = () => { setEditing(null); setForm(emptyForm()); setCouponSelected([]); };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, code: p.code, description: p.description || "", promo_type: p.promo_type || "discount",
      discount_pct: p.discount_pct ?? "", discount_amount: p.discount_amount ?? "",
      redeem_item_name: p.redeem_item_name || "", redeem_price: p.redeem_price ?? "", min_purchase: p.min_purchase ?? "",
      start_at: p.start_at?.slice(0, 10) || "", end_at: p.end_at?.slice(0, 10) || "",
      branch_ids: p.branch_ids || [], active: p.active, clinic_id: p.clinic_id || "",
    });
    // Reconstruct selected target items from saved ids
    const ids = new Set([...(p.target_treatment_ids || []).map((x) => `treatment:${x}`), ...(p.target_product_ids || []).map((x) => `product:${x}`)]);
    setCouponSelected(promoItems.filter((it) => ids.has(itemKey(it))));
    couponFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitCoupon = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.code || !form.start_at || !form.end_at) { toast.error("Field wajib belum lengkap"); return; }
    if (form.promo_type === "tebus_murah" && (!form.redeem_item_name || !form.redeem_price)) {
      toast.error("Tebus murah: item & harga tebus wajib diisi"); return;
    }
    if (isSuper && !editing && !form.clinic_id) { toast.error("Pilih Clinic tujuan promo"); return; }
    const payload = {
      ...form,
      discount_pct: form.discount_pct ? Number(form.discount_pct) : null,
      discount_amount: form.discount_amount ? Number(form.discount_amount) : null,
      redeem_price: form.redeem_price ? Number(form.redeem_price) : null,
      min_purchase: form.min_purchase ? Number(form.min_purchase) : null,
      target_treatment_ids: form.promo_type === "tebus_murah" ? [] : couponSelected.filter((x) => x.type === "treatment").map((x) => x.id),
      target_product_ids: form.promo_type === "tebus_murah" ? [] : couponSelected.filter((x) => x.type === "product").map((x) => x.id),
      branch_ids: form.branch_ids || [],
    };
    if (!(isSuper && !editing)) delete payload.clinic_id;
    setCouponBusy(true);
    try {
      if (editing) { await api.patch(`/promos/${editing.id}`, payload); toast.success("Kupon diperbarui & tersinkron ke POS"); }
      else { await api.post("/promos", payload); toast.success("Kupon dibuat & tersinkron ke POS"); }
      resetCoupon();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setCouponBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus kupon ini?")) return;
    await api.delete(`/promos/${id}`);
    toast.success("Dihapus");
    if (editing?.id === id) resetCoupon();
    refresh();
  };

  const today = new Date().toISOString().slice(0, 10);
  const isActive = (p) => p.active && p.start_at <= today && p.end_at >= today;

  return (
    <div className="space-y-6" data-testid="promos-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight">Promo & Diskon</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Buat paket promo / harga coret dan kode kupon — semua tersinkron real-time ke POS Transaksi per cabang.
        </p>
      </div>

      <Tabs defaultValue="item" className="w-full">
        <TabsList data-testid="promo-tabs">
          <TabsTrigger value="item" data-testid="tab-item-promo"><Package className="mr-1.5 h-4 w-4" /> Promo Paket & Harga Coret</TabsTrigger>
          <TabsTrigger value="coupon" data-testid="tab-coupon-promo"><Tag className="mr-1.5 h-4 w-4" /> Kode Kupon</TabsTrigger>
        </TabsList>

        {/* ============ ITEM / BUNDLE PROMO ============ */}
        <TabsContent value="item" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6 space-y-5" data-testid="item-promo-form">
                <div>
                  <h2 className="font-heading text-lg">Buat Promo / Paket Bundling</h2>
                  <p className="text-xs text-muted-foreground">Pilih satu item (harga coret) atau beberapa item sekaligus (bundling), lalu tentukan potongan.</p>
                </div>
                <div>
                  <Label>Nama Promo</Label>
                  <Input data-testid="item-promo-name" className="mt-1" value={promoName} onChange={(e) => setPromoName(e.target.value)} placeholder="cth: Promo Kemerdekaan Facial" />
                </div>
                <div>
                  <Label>Cari & Pilih Item (Produk / Treatment)</Label>
                  <ItemSearchPicker items={promoItems} selected={promoSelectedList} onToggle={togglePromoItem} testid="item-promo" />
                </div>

                {promoSelectedList.length > 0 && (
                  <div data-testid="item-promo-selected" className="rounded-lg border p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{promoSelectedList.length > 1 ? `Paket Bundling — ${promoSelectedList.length} item` : "1 item terpilih"}</div>
                      <div className="text-sm">Total Normal: <span className="font-semibold" data-testid="item-promo-normal-total">{fmtIDR(promoNormalTotal)}</span></div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {promoSelectedList.map((it) => (
                        <span key={itemKey(it)} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid={`item-promo-sel-${it.id}`}>
                          <Badge variant="outline" className="text-[10px]">{it.type === "treatment" ? "T" : "P"}</Badge>
                          {it.name} · {fmtIDR(it.normal_price)}
                          <button type="button" className="ml-1 text-rose-600" onClick={() => togglePromoItem(it)}>✕</button>
                        </span>
                      ))}
                    </div>
                    <div>
                      <Label className="text-xs">Tipe Potongan</Label>
                      <div className="mt-1 flex gap-2">
                        <button type="button" data-testid="promo-type-fixed" onClick={() => setPromoDiscType("fixed")}
                          className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${promoDiscType === "fixed" ? "bg-foreground text-background border-foreground" : "hover:bg-secondary"}`}>Harga Coret (Nominal)</button>
                        <button type="button" data-testid="promo-type-percent" onClick={() => setPromoDiscType("percent")}
                          className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${promoDiscType === "percent" ? "bg-foreground text-background border-foreground" : "hover:bg-secondary"}`}>Diskon Persentase (%)</button>
                      </div>
                    </div>
                    {promoDiscType === "fixed" ? (
                      <div>
                        <Label>Harga Promo Paket (Harga Coret)</Label>
                        <Input type="number" step="1" data-testid="item-promo-price" className="mt-1" value={promoPackagePrice} onChange={(e) => setPromoPackagePrice(e.target.value)} placeholder="cth: 799000" />
                      </div>
                    ) : (
                      <div>
                        <Label>Diskon (%)</Label>
                        <Input type="number" step="1" data-testid="item-promo-pct" className="mt-1" value={promoDiscPct} onChange={(e) => setPromoDiscPct(e.target.value)} placeholder="cth: 20" />
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      Harga akhir: <span className="line-through mr-1">{fmtIDR(promoNormalTotal)}</span>
                      <span className="text-emerald-600 font-semibold" data-testid="item-promo-final-preview">{fmtIDR(promoFinalPreview)}</span>
                      {promoDiscType === "percent" && promoDiscPct ? ` (Disc ${promoDiscPct}%)` : ""}
                    </div>
                  </div>
                )}

                <div className="rounded-lg border p-3 space-y-2">
                  <div>
                    <Label className="text-sm font-medium">Berlaku di Cabang</Label>
                    <p className="text-xs text-muted-foreground">Kosongkan = berlaku untuk SEMUA cabang. Promo muncul otomatis di POS cabang tsb.</p>
                  </div>
                  {branches.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {branches.map((b) => {
                        const on = promoBranchIds.includes(b.id);
                        return (
                          <button type="button" key={b.id} data-testid={`item-promo-branch-${b.id}`} onClick={() => toggleItemPromoBranch(b.id)}
                            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>{b.name}</button>
                        );
                      })}
                    </div>
                  ) : (<p className="text-xs text-muted-foreground italic">Tidak ada cabang.</p>)}
                </div>

                <Button data-testid="item-promo-submit" onClick={submitItemPromo} disabled={itemBusy} className="w-full">
                  <Plus className="mr-1.5 h-4 w-4" /> {itemBusy ? "Menyimpan..." : `Simpan ${promoSelectedList.length > 1 ? "Paket Bundling" : "Promo"}`}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg">Promo Aktif</h2>
                  <Badge variant="outline">{itemPromos.length}</Badge>
                </div>
                {itemPromos.length === 0 && (<div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">Belum ada promo item. Buat di sebelah kiri.</div>)}
                <div className="space-y-2">
                  {itemPromos.map((p) => (
                    <div key={p.id} className="flex items-start justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm" data-testid={`item-promo-row-${p.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-1.5 flex-wrap">
                          {p.name}
                          <Badge variant="outline" className="text-[10px]">{p.promo_kind === "bundle" ? `Bundling ${p.items?.length || 0} item` : (p.items?.[0]?.item_type === "treatment" ? "Treatment" : "Produk")}</Badge>
                          <Badge className="text-[10px] bg-sky-100 text-sky-800 hover:bg-sky-100">{p.discount_type === "percent" ? `Disc ${p.discount_pct}%` : "Harga Coret"}</Badge>
                          {p.branch_ids && p.branch_ids.length > 0 ? <Badge variant="outline" className="text-[10px]">{p.branch_ids.map(branchName).join(", ")}</Badge> : <Badge variant="outline" className="text-[10px]">Semua cabang</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{(p.items || []).map((i) => i.item_name).join(" + ")} — <span className="line-through">{fmtIDR(p.normal_total)}</span> <span className="text-emerald-700 font-medium">{fmtIDR(p.final_price)}</span></div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-rose-600 shrink-0" data-testid={`item-promo-del-${p.id}`} onClick={() => deleteItemPromo(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ COUPON PROMO (inline full page) ============ */}
        <TabsContent value="coupon" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Form */}
            <Card ref={couponFormRef}>
              <CardContent className="p-6 space-y-4" data-testid="coupon-form">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-lg">{editing ? "Edit Kode Kupon" : "Buat Kode Kupon"}</h2>
                    <p className="text-xs text-muted-foreground">Kupon dengan kode yang diketik pelanggan/kasir saat transaksi di POS.</p>
                  </div>
                  {editing && (<Button size="sm" variant="outline" data-testid="coupon-reset-btn" onClick={resetCoupon}>Batal Edit</Button>)}
                </div>

                <form onSubmit={submitCoupon} className="space-y-4">
                  {isSuper && !editing && (
                    <div>
                      <Label>Clinic Tujuan *</Label>
                      <select data-testid="promo-clinic-select" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.clinic_id} onChange={(e) => setForm({ ...form, clinic_id: e.target.value, branch_ids: [] })}>
                        <option value="">— Pilih Clinic —</option>
                        {clinics.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                      </select>
                    </div>
                  )}
                  {isSuper && editing && (<div className="text-xs text-muted-foreground bg-secondary rounded px-2 py-1.5">Clinic promo ini tidak dapat dipindahkan setelah dibuat.</div>)}

                  <div>
                    <Label>Nama Promo *</Label>
                    <Input data-testid="promo-name-input" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: Diskon Pembukaan" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Kode *</Label>
                      <Input data-testid="promo-code-input" className="mt-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME20" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
                        <option value="yes">Aktif</option>
                        <option value="no">Non-aktif</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Deskripsi</Label>
                    <Textarea className="mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                  </div>
                  <div>
                    <Label>Jenis Promo</Label>
                    <select data-testid="promo-type-select" className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.promo_type} onChange={(e) => setForm({ ...form, promo_type: e.target.value })}>
                      <option value="discount">Diskon (persen / nominal)</option>
                      <option value="tebus_murah">Tebus Murah</option>
                    </select>
                  </div>

                  {form.promo_type === "tebus_murah" ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label>Item yang Ditebus *</Label>
                        <Input data-testid="promo-redeem-item-input" className="mt-1" value={form.redeem_item_name} onChange={(e) => setForm({ ...form, redeem_item_name: e.target.value })} placeholder="Serum Brightening 30ml" />
                      </div>
                      <div>
                        <Label>Harga Tebus (Rp) *</Label>
                        <Input type="number" min="0" step="1" data-testid="promo-redeem-price-input" className="mt-1" value={form.redeem_price} onChange={(e) => setForm({ ...form, redeem_price: e.target.value })} placeholder="50000" />
                      </div>
                      <div>
                        <Label>Min. Belanja (Rp)</Label>
                        <Input type="number" min="0" step="1" data-testid="promo-min-purchase-input" className="mt-1" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} placeholder="500000" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Diskon %</Label>
                          <Input type="number" min="0" max="100" className="mt-1" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value, discount_amount: "" })} placeholder="20" />
                        </div>
                        <div>
                          <Label>Potongan (Rp)</Label>
                          <Input type="number" min="0" step="1" className="mt-1" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value, discount_pct: "" })} placeholder="500000" />
                        </div>
                      </div>

                      <div className="rounded-lg border p-3 space-y-2" data-testid="promo-target-section">
                        <div>
                          <Label className="text-sm font-medium">Berlaku untuk item tertentu (opsional) — dukung bundling</Label>
                          <p className="text-xs text-muted-foreground">Kosongkan = berlaku ke SEMUA item. Cari & pilih beberapa treatment/produk agar diskon dihitung dari total item tsb.</p>
                        </div>
                        <ItemSearchPicker items={promoItems} selected={couponSelected} onToggle={toggleCouponItem} testid="coupon-target" />
                        {couponSelected.length > 0 && (
                          <div className="space-y-2 pt-1" data-testid="coupon-target-selected">
                            <div className="flex flex-wrap gap-1.5">
                              {couponSelected.map((it) => (
                                <span key={itemKey(it)} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid={`coupon-target-sel-${it.id}`}>
                                  <Badge variant="outline" className="text-[10px]">{it.type === "treatment" ? "T" : "P"}</Badge>
                                  {it.name} · {fmtIDR(it.normal_price)}
                                  <button type="button" className="ml-1 text-rose-600" onClick={() => toggleCouponItem(it)}>✕</button>
                                </span>
                              ))}
                            </div>
                            <div className="text-sm">{couponSelected.length > 1 ? `Paket Bundling — ${couponSelected.length} item · ` : ""}Total Harga Normal: <span className="font-semibold" data-testid="coupon-target-normal-total">{fmtIDR(couponNormalTotal)}</span></div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="rounded-lg border p-3 space-y-2" data-testid="promo-branch-wrap">
                    <div>
                      <Label className="text-sm font-medium">Cabang Berlaku</Label>
                      <p className="text-xs text-muted-foreground">Kosongkan = berlaku untuk SEMUA cabang.</p>
                    </div>
                    {clinicBranches.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {clinicBranches.map((b) => {
                          const on = (form.branch_ids || []).includes(b.id);
                          return (
                            <button type="button" key={b.id} data-testid={`promo-branch-${b.id}`} onClick={() => toggleCouponBranch(b.id)}
                              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>{b.name}</button>
                          );
                        })}
                      </div>
                    ) : (<p className="text-xs text-muted-foreground italic">{isSuper && !form.clinic_id ? "Pilih clinic dulu untuk melihat cabang." : "Tidak ada cabang."}</p>)}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Mulai *</Label>
                      <Input type="date" data-testid="promo-start-input" className="mt-1" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
                    </div>
                    <div>
                      <Label>Selesai *</Label>
                      <Input type="date" data-testid="promo-end-input" className="mt-1" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
                    </div>
                  </div>

                  <Button type="submit" data-testid="promo-save-btn" disabled={couponBusy} className="w-full">
                    <Plus className="mr-1.5 h-4 w-4" /> {couponBusy ? "Menyimpan..." : (editing ? "Simpan Perubahan" : "Simpan Kode Kupon")}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* List */}
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg">Kupon Tersimpan</h2>
                  <Badge variant="outline">{promos.length}</Badge>
                </div>
                {promos.length === 0 && (<div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">Belum ada kupon. Buat di sebelah kiri.</div>)}
                <div className="space-y-2">
                  {promos.map((p) => {
                    const active = isActive(p);
                    return (
                      <div key={p.id} data-testid={`promo-card-${p.id}`} className={`rounded-lg border px-3 py-2.5 ${active ? "border-emerald-300" : "opacity-70"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Berjalan</Badge> : <Badge variant="outline" className="text-[10px]"><XCircle className="h-2.5 w-2.5 mr-0.5" /> Off</Badge>}
                              <span className="font-medium">{p.name}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <code className="rounded bg-secondary px-2 py-0.5 text-xs font-bold tracking-wider">{p.code}</code>
                              <button onClick={() => { navigator.clipboard?.writeText(p.code); toast.success("Kode disalin"); }} className="text-muted-foreground hover:text-foreground" title="Salin kode"><Copy className="h-3 w-3" /></button>
                              <span className="text-xs font-medium">{p.discount_pct ? `Diskon ${p.discount_pct}%` : ""}{p.discount_amount ? `Potongan ${fmtIDR(p.discount_amount)}` : ""}{p.promo_type === "tebus_murah" ? "Tebus Murah" : ""}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground">
                              <span>{fmtDate(p.start_at)} → {fmtDate(p.end_at)}</span>
                              <Badge variant="outline" className="text-[10px]">{(p.branch_ids || []).length > 0 ? p.branch_ids.map(branchName).join(", ") : "Semua cabang"}</Badge>
                              {((p.target_treatment_ids || []).length + (p.target_product_ids || []).length) > 0 && (
                                <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`promo-target-badge-${p.id}`}>Khusus {(p.target_treatment_ids || []).length + (p.target_product_ids || []).length} item</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="outline" data-testid={`edit-promo-${p.id}`} onClick={() => openEdit(p)}><Pencil className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" data-testid={`delete-promo-${p.id}`} onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
